import axios from "axios";

const state = () => ({
  course: null,
  permissions: {},
  loading: false,
  loading_course: false,
  loading_associates: false,
  loading_permissions: false,
  errors_permissions: null,
  message_permissions: null,
  errors: null,
  message: "",
  outlines: [],
  course_updating: false,
  rerun_rounds_loading: false,
});

const mutations = {
  SET_COURSE(state, course) {
    state.course = course;
  },
  SET_LOADING(state) {
    state.loading = true;
  },
  SET_LOADING_COURSE(state) {
    state.loading_course = true;
  },
  SET_RERUN_ROUNDS_LOADING(state, loading) {
    state.rerun_rounds_loading = loading;
  },
  SET_LOADING_ASSOCIATES(state, loading) {
    state.loading_associates = loading;
  },
  // permissions
  SET_PERMISSIONS(state, permissions) {
    state.permissions = permissions;
  },
  SET_LOADING_PERMISSIONS(state) {
    state.loading_permissions = true;
  },
  CLEAR_LOADING_PERMISSIONS(state) {
    state.loading_permissions = false;
  },
  SET_ERRORS_PERMISSIONS(state, errors) {
    state.errors_permissions = errors;
  },
  SET_MESSAGE_PERMISSIONS(state, message) {
    state.message = message;
  },
  CLEAR_LOADING(state) {
    state.loading = false;
  },
  CLEAR_LOADING_COURSE(state) {
    state.loading_course = false;
  },
  SET_ERRORS(state, errors) {
    state.errors = errors;
  },
  SET_MESSAGE(state, message) {
    state.message = message;
  },
  SET_COURSE_UPDATING(state, updating) {
    state.course_updating = updating;
  },
};

const getters = {
  getCourse: (state) => {
    return state.course;
  },
  getPermissions: (state) => {
    return state.permissions;
  },
  isLoading: (state) => {
    return state.loading;
  },
  isLoadingCourse: (state) => {
    return state.loading_course;
  },
  isRerunRoundsLoading: (state) => {
    return state.rerun_rounds_loading;
  },
  isLoadingAssociates: (state) => {
    return state.loading_associates;
  },
  getErrors: (state) => {
    return state.errors;
  },
  getMessage: (state) => {
    return state.message;
  },
  isCourseUpdating: (state) => {
    return state.course_updating;
  },
  getMessagePermissions: (state) => {
    return state.message_permissions;
  },
  getErrorsPermissions: (state) => {
    return state.errors_permissions;
  },
  isLoadingPermissions: (state) => {
    return state.loading_permissions;
  },
};

const actions = {
  async fetchCourse({ commit }, id) {
    commit("SET_LOADING_COURSE");
    const url = process.env.VUE_APP_API_URL + "/courses/courseDetails/" + id;
    const config = {
      headers: {
        Accept: "application/json",
        Authorization: "Bearer " + localStorage.getItem("user_token"),
      },
    };
    try {
      const response = await axios.get(url, config);
      commit("SET_COURSE", response.data);
      commit("CLEAR_LOADING_COURSE");
    } catch (error) {
      console.log(error);
      commit("SET_COURSE", null);
      commit("CLEAR_LOADING_COURSE");
    }
  },

  async migrateCourse({ commit }, payload) {
    commit("SET_LOADING");
    const url = process.env.VUE_APP_API_URL + "/tm/courses/addCourse";
    const config = {
      headers: {
        Accept: "application/json",
        Authorization: "Bearer " + localStorage.getItem("user_token"),
      },
    };
    try {
      const response = await axios.post(url, payload, config);
      commit("SET_ERRORS", null);
      commit("CLEAR_LOADING");
      commit("SET_MESSAGE", "Course successfully added to 4.0!");
      return response;
    } catch (error) {
      console.log(error);
      commit("CLEAR_LOADING");
      commit("SET_MESSAGE", "");
      commit(
        "SET_ERRORS",
        error.response?.data?.message || "Failed to add course to 4.0"
      );
    }
  },

  async fetchPermissions({ commit }, courseId) {
    if (!courseId) {
      console.error("Course ID not available");
      return;
    }

    commit("SET_LOADING");
    try {
      const response = await axios.get(
        `${process.env.VUE_APP_API_URL}/tm/courses/${courseId}/permissions`,
        {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${localStorage.getItem("user_token")}`,
          },
        }
      );
      commit("SET_PERMISSIONS", response.data);
    } catch (error) {
      console.error("Load permissions error:", error);
      commit("SET_PERMISSIONS", {});
    } finally {
      commit("CLEAR_LOADING");
    }
  },

  async updatePermissions({ commit }, { courseId, permissions }) {
    if (!courseId) {
      console.error("Course ID not available");
      return;
    }

    commit("SET_LOADING_PERMISSIONS");
    try {
      await axios.post(
        `${process.env.VUE_APP_API_URL}/tm/courses/${courseId}/permissions`,
        { ids: permissions },
        {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${localStorage.getItem("user_token")}`,
          },
        }
      );
      commit("SET_MESSAGE_PERMISSIONS", "Permissions updated successfully");
      commit("SET_ERRORS_PERMISSIONS", null);
    } catch (error) {
      console.error("Update permissions error:", error);
      commit("SET_MESSAGE_PERMISSIONS", null);
      commit(
        "SET_ERRORS_PERMISSIONS",
        error.response?.data?.message || "Failed to update permissions"
      );
    } finally {
      commit("CLEAR_LOADING_PERMISSIONS");
    }
  },

  async updateAssociates({ commit }, { courseId, associates }) {
    commit("SET_LOADING_ASSOCIATES", true);
    const url = `${process.env.VUE_APP_API_URL}/courses/${courseId}/associates`;
    try {
      await axios.post(
        url,
        { associates },
        {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${localStorage.getItem("user_token")}`,
          },
        }
      );
      commit("SET_MESSAGE", "Associates updated successfully");
      commit("SET_ERRORS", null);
    } catch (error) {
      console.error("Update associates error:", error);
      commit(
        "SET_ERRORS",
        error.response?.data?.message || "Failed to update associates"
      );
    } finally {
      commit("SET_LOADING_ASSOCIATES", false);
    }
  },

  async updateCourse({ commit }, { courseId, data }) {
    commit("SET_COURSE_UPDATING", true);
    const url = `${process.env.VUE_APP_API_URL}/tm/courses/${courseId}`;
    const config = {
      headers: {
        Accept: "application/json",
        Authorization: "Bearer " + localStorage.getItem("user_token"),
      },
    };
    try {
      await axios.patch(url, data, config);
      commit("SET_ERRORS", null);
      commit("SET_MESSAGE", "Course updated successfully");
      //await dispatch("fetchCourse", courseId);
    } catch (error) {
      console.log(error);
      commit(
        "SET_ERRORS",
        error.response?.data?.message || "Failed to update course"
      );
    } finally {
      commit("SET_COURSE_UPDATING", false);
    }
  },

  async createUpdateTmUser({ commit }, courseId) {
    if (!courseId) {
      console.error("Course ID not available");
      return;
    }
    const url = `${process.env.VUE_APP_API_URL}/tm/courses/create-update-tm-user`;
    const config = {
      headers: {
        Accept: "application/json",
        Authorization: "Bearer " + localStorage.getItem("user_token"),
      },
    };
    try {
      const response = await axios.post(url, { courseId }, config);
      commit(
        "toastNotification/SET_MODAL",
        {
          title: "TM user",
          message:
            response.data?.message || "TM user created/updated successfully!",
          type: "success",
        },
        { root: true }
      );
    } catch (error) {
      commit(
        "toastNotification/SET_MODAL",
        {
          title: "TM user",
          message:
            error.response?.data?.message || "Failed to create/update TM user",
          type: "error",
        },
        { root: true }
      );
    }
  },

  async rerunRounds({ commit }, { courseId, start_date, end_date }) {
    commit("SET_RERUN_ROUNDS_LOADING", true);
    const url = `${process.env.VUE_APP_API_URL}/courses/${courseId}/rerun`;
    const config = {
      headers: {
        Accept: "application/json",
        Authorization: "Bearer " + localStorage.getItem("user_token"),
      },
    };
    try {
      const response = await axios.post(url, { start_date, end_date }, config);
      commit(
        "toastNotification/SET_MODAL",
        {
          title: "Re-run successful",
          message: response.data.message || "Rounds rerun successfully!",
          type: "success",
        },
        { root: true }
      );
    } catch (error) {
      commit(
        "toastNotification/SET_MODAL",
        {
          title: "Re-run failed",
          message: error.response?.data?.message || "Rerun rounds failed",
          type: "error",
        },
        { root: true }
      );
    } finally {
      commit("SET_RERUN_ROUNDS_LOADING", false);
    }
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions,
};
